import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Active } from 'types';
import { getProductDisplayName } from '../../../../mappers/serviceAgreementToTreeMapper';
import { LinkContactNotesButton } from '../../../../LinkContactNotesButton';
import { useSaveServiceRolesAndNotify } from '../../../hooks/useSaveServiceRolesAndNotify';
import { formatProductNameList } from '../../../utils/productNameUtils';
import { DEFAULT_SAVE_STATE, getSaveGate, SaveState } from '../../../utils/saveGate';
import { ClosureOnSaveConfirmation } from '../../components/ClosureOnSaveConfirmation';
import { CloseProductSection } from '../../components/CloseProductSection';
import { PartnerSelectionSection } from '../../components/PartnerSelectionSection';
import { useCloseConfigureModal } from '../../hooks/useCloseConfigureModal';
import { useCloseServiceAgreement } from '../../hooks/useCloseServiceAgreement';
import { useClosureConfirmingSave } from '../../hooks/useClosureConfirmingSave';
import { useGenericCloseBodyText } from '../../hooks/useGenericCloseBodyText';
import { useProductContactNotes } from '../../hooks/useProductContactNotes';
import { ConfigureProductDialog } from '../../shell/ConfigureProductDialog';
import type { ConfigureProductGroupModalProps } from '../configureProductGroupModalProps';
import { getPartnerServiceRoleProductConfig } from './partnerServiceRoleProductConfig';

const MODAL_WIDTH = '1080px';

export const PartnerServiceRoleModal: React.FC<ConfigureProductGroupModalProps> = ({
  serviceAgreement,
  brUid,
  relatedServiceAgreementNames,
  relatedServiceAgreementUids,
}) => {
  const { t } = useTranslation();
  const config = getPartnerServiceRoleProductConfig(serviceAgreement.serviceDefinitionId);
  const [saveState, setSaveState] = useState<SaveState>(DEFAULT_SAVE_STATE);
  const { saveAndNotify, isPending: isClosingOnSave } = useSaveServiceRolesAndNotify();
  const { close, isClosing } = useCloseServiceAgreement(serviceAgreement, brUid);
  const genericCloseBodyText = useGenericCloseBodyText();
  const contactNotes = useProductContactNotes({
    serviceAgreement,
    brUid,
    addsServiceRoles: saveState.addsServiceRoles,
    linksContactNotes: config.linksContactNotes,
  });
  const { canSave, reasons: saveBlockedReasons } = getSaveGate([saveState.blocker, contactNotes.blocker]);
  const { isPending } = saveState;
  const handleClose = useCloseConfigureModal(isPending);

  const relatedProductNames = [getProductDisplayName(serviceAgreement), ...relatedServiceAgreementNames].filter(
    (name): name is string => Boolean(name),
  );
  const closeProductBodyText =
    relatedProductNames.length > 0
      ? t('pb.deleteproductmodal.closeproduct.relatedproducts', {
          productNames: formatProductNameList(relatedProductNames),
        })
      : genericCloseBodyText;

  const { handleSave, isConfirmingClosure, confirmClosure, cancelClosure } = useClosureConfirmingSave({
    saveState,
    canSave,
    isBusy: isClosingOnSave,
    confirm: async () => {
      await saveAndNotify({
        serviceAgreementUid: serviceAgreement.serviceAgreementId,
        partnerIds: [],
        contactNoteUids: contactNotes.uidsForSave,
        relatedServiceAgreementUids,
        successToastTitle: t('pb.closeactiveebankingproductmodal.success.message', 'Product closed successfully.'),
        closeBeforeErrorModal: true,
      });
    },
  });

  const disabledCloseReason =
    serviceAgreement.agreementStatus === Active.ACTIVE ? t('pb.configureproductmodal.active.close.tooltip') : null;

  return (
    <>
      <ConfigureProductDialog
        title={t('pb.configureproductmodal.title', 'Edit product')}
        width={MODAL_WIDTH}
        size="large"
        isPending={isPending}
        onClose={handleClose}
        footer={{ canSave, saveBlockedReasons, onSave: handleSave }}
        editor={
          <PartnerSelectionSection
            serviceAgreement={serviceAgreement}
            brUid={brUid}
            contactNoteUids={contactNotes.uidsForSave}
            allowAllPartnersSearch={config.allowAllPartnersSearch}
            extraPartnerSearchColumns={config.extraPartnerSearchColumns}
            onSaveStateChange={setSaveState}
          />
        }
      >
        {contactNotes.linksContactNotes && (
          <LinkContactNotesButton
            brUuid={brUid}
            newlyLinkedNotes={contactNotes.notes}
            onLinkedNotesChange={contactNotes.changeNotes}
            existingNoteIds={contactNotes.existingNoteIds}
            addTopMargin
            showContactNoteRequired={contactNotes.blocker.blocked}
            requiredAlertText={contactNotes.blocker.reason ?? undefined}
          />
        )}
        <CloseProductSection
          isPending={isPending}
          bodyText={closeProductBodyText}
          addTopMargin
          disabledReason={disabledCloseReason}
          isClosing={isClosing}
          onConfirmClose={close}
        />
      </ConfigureProductDialog>
      {isConfirmingClosure && (
        <ClosureOnSaveConfirmation
          productNames={relatedProductNames}
          isPending={isClosingOnSave || isPending}
          onConfirm={confirmClosure}
          onCancel={cancelClosure}
        />
      )}
    </>
  );
};
